/**
 * قطع صغيرة مشتركة بين شاشات السجل (الطلاب · سجل الطالب · المعلمين · ملف المعلم).
 *
 * ⛔ قاعدتان بيتنفّذوا هنا وبس:
 *   1) **الخانة الفاضية بتفضل فاضية** — «—» رمادية. مفيش «غير مذكور» ولا
 *      صفر مخترَع ولا تاريخ مخمّن.
 *   2) ⚠️ فخّ الـbidi: `<bdi dir="ltr">` جوّاه حرف عربي **بيقلب المقاطع**
 *      («18 – 28 أغسطس» بتتقرا مقلوبة = فترة غلط). كل التواريخ هنا في
 *      `<bdi>` **بلا dir** — والـ`dir="ltr"` للاتيني الصافي (`<Ltr>`) وبس.
 */
import type { ReactNode } from "react";
import { fmtDateShort, fmtNum, Ltr } from "@/lib/format";

/** الغياب المعلن — مش صفر ولا نص مخترَع */
export function Dash() {
  return <span className="missing">—</span>;
}

/** نص لو موجود، وإلا «—» */
export function Maybe({ v }: { v: string | null | undefined }) {
  return v && v.trim() ? <>{v}</> : <Dash />;
}

/** تاريخ قصير: «15 أغسطس» — `<bdi>` بلا dir لأن اسم الشهر عربي */
export function DateAr({ v }: { v: string | null | undefined }) {
  if (!v) return <Dash />;
  const d = new Date(v);
  if (Number.isNaN(d.getTime())) {
    // ⛔ ممنوع الفشل الصامت — تاريخ مش مقروء بيتقال، ما بيتحوّلش لـ«—»
    console.error("[registry] تاريخ مش مقروء جاي من السيستم:", v);
    return <span className="missing">تاريخ مش مقروء</span>;
  }
  return <bdi className="num">{fmtDateShort(d)}</bdi>;
}

/**
 * فترة: «18 – 28 أغسطس».
 * الشرطة U+2013 بمسافات عاديّة، وكله جوّه `<bdi>` **بلا dir**.
 */
export function DateRangeAr({
  from,
  to,
  endUnknown = false,
}: {
  from: string | null;
  to: string | null;
  endUnknown?: boolean;
}) {
  const start = from ? new Date(from) : null;
  const end = to ? new Date(to) : null;
  const startOk = start !== null && !Number.isNaN(start.getTime());
  const endOk = end !== null && !Number.isNaN(end.getTime());

  if (!startOk && !endOk) {
    return endUnknown ? <span className="missing">النهاية مش محددة</span> : <Dash />;
  }
  if (startOk && !endOk) {
    return (
      <>
        <bdi className="num">{fmtDateShort(start)}</bdi>{" "}
        {endUnknown ? (
          // 🔑 «مش محددة» ≠ «الخانة مش متملّية» — الفرق موجود في الداتا فعلًا
          <span className="missing">— النهاية مش محددة</span>
        ) : (
          <span className="missing">— بلا تاريخ نهاية</span>
        )}
      </>
    );
  }
  if (!startOk && endOk) {
    return (
      <>
        <span className="missing">بلا تاريخ بداية —</span>{" "}
        <bdi className="num">{fmtDateShort(end)}</bdi>
      </>
    );
  }
  // الاتنين موجودين
  const sameMonth =
    start!.getFullYear() === end!.getFullYear() && start!.getMonth() === end!.getMonth();
  const startText = sameMonth ? fmtNum(start!.getDate()) : fmtDateShort(start!);
  return <bdi className="num">{`${startText} – ${fmtDateShort(end!)}`}</bdi>;
}

/**
 * «آخر تواصل»: اليوم · أمس · من 3 أيام · من 30 يومًا.
 * ⛔ `null` بيفضل «—» — ما بنخترعش «من زمان».
 * ⚠️ الفرق بالأيام التقويمية بتوقيت الجهاز — تقريب مقصود ومعلَن، والتاريخ
 *    الكامل في `title` للي عايز الدقة.
 */
export function SinceAr({ at }: { at: string | null | undefined }) {
  if (!at) return <Dash />;
  const then = new Date(at);
  if (Number.isNaN(then.getTime())) {
    console.error("[registry] «آخر تواصل» مش تاريخ مقروء:", at);
    return <span className="missing">تاريخ مش مقروء</span>;
  }
  const days = calendarDaysAgo(then);
  const text =
    days <= 0
      ? "اليوم"
      : days === 1
        ? "أمس"
        : days === 2
          ? "من يومين"
          : days <= 10
            ? `من ${fmtNum(days)} أيام`
            : `من ${fmtNum(days)} يومًا`;
  return (
    <span title={fmtDateShort(then)} style={{ whiteSpace: "nowrap" }}>
      {text}
    </span>
  );
}

function calendarDaysAgo(then: Date, now: Date = new Date()): number {
  const a = new Date(now.getFullYear(), now.getMonth(), now.getDate()).getTime();
  const b = new Date(then.getFullYear(), then.getMonth(), then.getDate()).getTime();
  return Math.round((a - b) / 86_400_000);
}

/**
 * وسم «محتاج استكمال».
 * ⚠️ رمادي عمدًا (`.src`) مش شارة حالة ملوّنة: الصف عنده حالة واحدة بلون
 *    واحد ونقطة واحدة، والنقص ده **جودة داتا** مش حالة طالب.
 */
/**
 * ⚠️ النوع هنا **بالضبط اللي المكوّن بيقراه** مش `DataQuality` كاملة:
 *    `DataQuality` بتحقّقه هيكليًا، وأي وسم جودة تاني في السيستم (النطاق
 *    المالي مثلًا) بيشتغل من غير تحويل نوع كاذب.
 */
export function NeedsReviewMark(
  { q, compact = false }: { q: { needs_review: boolean; issues_ar: string[] }; compact?: boolean },
) {
  if (!q.needs_review) return null;
  const why = q.issues_ar.length > 0 ? q.issues_ar.join(" · ") : "السبب مش مكتوب في السجل";
  return (
    <span className="src" title={why}>
      محتاج استكمال
      {compact || q.issues_ar.length === 0 ? null : (
        <>
          {" "}
          <span className="num">{fmtNum(q.issues_ar.length)}</span>
        </>
      )}
    </span>
  );
}

/**
 * تصنيف الأسرة (VIP ومستواه).
 *
 * ⚠️ فخّ الـbidi: «VIP ذهبي» فيه لاتيني وعربي في سطر واحد — فـ«VIP» لوحدها
 *    في `<Ltr>` والمستوى العربي في `<span>` عادي. `<bdi dir="ltr">` جوّاه
 *    حرف عربي بيقلب المقاطع (القاعدة في ديباجة الملف).
 * ⛔ والوسوم اللي بتوصل هنا **مفلترة من السيرفر** — الواجهة ما بتقررش أنهي
 *    وسم يتعرض، عشان ما يبقاش فيه تطبيقان لنفس القاعدة.
 */
export function VipMark({ tagsAr }: { tagsAr: { key: string; label_ar: string }[] }) {
  const vip = tagsAr.find((t) => t.key === "vip");
  if (!vip) return null;
  const level = tagsAr.find((t) => t.key === "vip_gold_card" || t.key === "vip_silver_card");
  // «ذهبي» / «فضي» — الكلمة العربية لوحدها من ليبل السيرفر بلا إعادة كتابة
  const levelWord = level ? level.label_ar.replace("VIP", "").trim() : "";
  const why = tagsAr.map((t) => t.label_ar).join(" · ");
  return (
    <span className="chip vip" title={why}>
      <Ltr>VIP</Ltr>
      {levelWord ? <span>{levelWord}</span> : null}
    </span>
  );
}

/** الحرف الأول في دايرة — زي التصميم (28px في الجدول · 48px في الهيدر) */
export function Avatar({ name, size = 28 }: { name: string; size?: number }) {
  const letter = firstLetter(name);
  return (
    <div
      className="avatar"
      aria-hidden="true"
      style={{ width: size, height: size, fontSize: Math.max(12, Math.round(size * 0.42)) }}
    >
      {letter}
    </div>
  );
}

function firstLetter(name: string): string {
  const trimmed = (name || "").trim();
  if (!trimmed) return "؟";
  return [...trimmed][0] ?? "؟";
}

/** عنوان قسم + محتوى — نفس `.sec-h`/`.sec-title` المعتمدين */
export function Section({
  title,
  note,
  action,
  children,
}: {
  title: string;
  note?: ReactNode;
  action?: ReactNode;
  children: ReactNode;
}) {
  return (
    <div className="card" style={{ padding: 20, display: "flex", flexDirection: "column", gap: 12 }}>
      <div className="sec-h">
        <span className="sec-title">{title}</span>
        {note ? <span className="sec-note">{note}</span> : null}
        {action ? <span style={{ marginRight: "auto" }}>{action}</span> : null}
      </div>
      {children}
    </div>
  );
}

/** صف «مفتاح: قيمة» */
export function DefRow({ k, children }: { k: string; children: ReactNode }) {
  return (
    <div className="defrow">
      <span className="k">{k}</span>
      <span className="v">{children}</span>
    </div>
  );
}

/**
 * قسم محجوب عن قسم المستخدم.
 * ⛔ **مش** قايمة فاضية: «مفيش اشتراكات» كذب لو الحقيقة «مش من حقك تشوفها».
 */
export function HiddenSection({ what }: { what: string }) {
  return (
    <div className="t-desc" role="status">
      {what} محجوبة عن قسمك — مش قايمة فاضية.
    </div>
  );
}
